import SharesIcon from '@src/svgs/SharesIcon';
import { forwardRef } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { MODAL_TOOLTIP_STYLE } from '../common/tooltipStyles';

export const CLIP_PUBLISH_GATE_TOOLTIP_ID = 'clip-publish-gate-tooltip';

type Props = {
  /** Publishing is not allowed right now (renditions still processing,
   *  audio enhancement finishing) — same gate as Creator Studio's Publish. */
  blocked: boolean;
  /** Why it is blocked; shown on hover over the disabled button. */
  tooltip?: string;
  onClick: () => void;
};

/**
 * The Clip Editor's green "Publish and Share" CTA (opens the ExportMenu).
 * While the publish gate is closed the button is disabled and explains itself
 * on hover, exactly like Creator Studio's Publish: a disabled <button> swallows
 * pointer events, so the tooltip anchors on the wrapper instead.
 */
export const ClipPublishShareButton = forwardRef<HTMLButtonElement, Props>(
  ({ blocked, tooltip, onClick }, ref) => (
    <GateAnchor
      data-testid="clip-publish-share-gate"
      data-tooltip-id={CLIP_PUBLISH_GATE_TOOLTIP_ID}
      data-tooltip-content={blocked ? tooltip : undefined}
    >
      <PrimaryButton
        ref={ref}
        type="button"
        data-testid="clip-preview-export-button"
        disabled={blocked}
        onClick={onClick}
      >
        <SharesIcon
          width="16"
          height="16"
          strokeColor="currentColor"
          strokeWidth="2"
        />{' '}
        Publish and Share
      </PrimaryButton>
      {/* Mounted only while blocked, as the export menu does: nothing to say
          when the button is live. */}
      {blocked && tooltip && (
        <Tooltip
          id={CLIP_PUBLISH_GATE_TOOLTIP_ID}
          style={MODAL_TOOLTIP_STYLE}
          place="top"
        />
      )}
    </GateAnchor>
  ),
);
ClipPublishShareButton.displayName = 'ClipPublishShareButton';

/* Takes the footer slot the bare button used to (flex: 1, half the row) so the
   wrapper is invisible to the ButtonGroup layout. */
const GateAnchor = styled.span`
  display: flex;
  flex: 1;
  max-width: 50%;
`;

const PrimaryButton = styled.button`
  position: relative;
  display: flex;
  flex: 1;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  background-color: var(--primary);
  color: var(--background);
  font-weight: var(--font-weight-semi-bold);

  &:hover:not(:disabled) {
    background-color: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`;

export default ClipPublishShareButton;
